import { AttributeType } from "@/models/attribute";
import { AttributeFilter } from "@/models/attributeFilter";

export type ChatAttributeFilter = {
  filter: AttributeFilter;
  error?: ChatAttributeFilterErrorKey;
  isAll?: boolean; // すべての値が選択されている場合に true
};

export type ChatAttributeFilters = Record<string, ChatAttributeFilter>;

export type ChatAttributeFilterErrorKey =
  | "chat.filter.errors.numerical.missingRange"
  | "chat.filter.errors.numerical.invalidRange"
  | "chat.filter.errors.categorical.noSelection"
  | "chat.filter.errors.textual.empty";

export const createChatNumericalAttributeFilter = (
  gte: number | null | undefined,
  lte: number | null | undefined,
): ChatAttributeFilter => {
  const normalizedGte = typeof gte === "number" ? gte : null;
  const normalizedLte = typeof lte === "number" ? lte : null;

  let error: ChatAttributeFilterErrorKey | undefined;
  if (normalizedGte === null && normalizedLte === null) {
    error = "chat.filter.errors.numerical.missingRange";
  } else if (normalizedGte !== null && normalizedLte !== null && normalizedGte > normalizedLte) {
    error = "chat.filter.errors.numerical.invalidRange";
  }

  return {
    filter: {
      type: AttributeType.NUMERICAL,
      gte: normalizedGte,
      lte: normalizedLte,
    },
    error,
  };
};

export const createChatCategoricalAttributeFilter = (
  values: string[],
  allValues?: string[],
): ChatAttributeFilter => {
  const selectedValues = values.filter(value => value && value.length > 0);
  const error = selectedValues.length === 0
    ? "chat.filter.errors.categorical.noSelection"
    : undefined;

  // すべての値が選択されているかチェック
  const isAll = allValues !== undefined
    && selectedValues.length === allValues.length
    && selectedValues.length > 0;

  return {
    filter: {
      type: AttributeType.CATEGORICAL,
      values: selectedValues,
    },
    error,
    isAll,
  };
};

export const createChatTextualAttributeFilter = (
  value: string,
): ChatAttributeFilter => {
  const error = value.trim().length === 0
    ? "chat.filter.errors.textual.empty"
    : undefined;

  return {
    filter: {
      type: AttributeType.TEXTUAL,
      value,
    },
    error,
  };
};

/**
 * エラーのない属性フィルターのみを抽出してAttributeFiltersに変換
 * すべての値が選択されている場合（isAll: true）はフィルターを除外
 */
export const getValidAttributeFilters = (
  chatFilters: ChatAttributeFilters,
): Record<string, AttributeFilter> => {
  const validFilters: Record<string, AttributeFilter> = {};

  for (const [attributeName, chatFilter] of Object.entries(chatFilters)) {
    // エラーがない かつ すべてが選択されていない場合のみ含める
    if (!chatFilter.error && !chatFilter.isAll) {
      validFilters[attributeName] = chatFilter.filter;
    }
  }

  return validFilters;
};
